import { CoolingSystemIcon } from "@/components/icons/amenities/CoolingSystemIcon";
import { ElevatorIcon } from "@/components/icons/amenities/ElevatorIcon";
import { InternetAndSignalIcon } from "@/components/icons/amenities/InternetAndSignalIcon";
import { IranianToiletIcon } from "@/components/icons/amenities/IranianToiletIcon";
import { JacuzziIcon } from "@/components/icons/amenities/JacuzziIcon";
import { SaunaIcon } from "@/components/icons/amenities/SaunaIcon";
import { SwimmingPoolIcon } from "@/components/icons/amenities/SwimmingPoolIcon";
import { WesternToiletIcon } from "@/components/icons/amenities/WesternToiletIcon";
import {
  Bathtub01Icon,
  BedIcon,
  Home01Icon,
  ParkingAreaSquareIcon,
  Stairs01Icon,
} from "@hugeicons/core-free-icons";
import { IconSvgElement } from "@hugeicons/react";
import { ComponentType, SVGProps } from "react";

export type AmenityCustomIcon = ComponentType<SVGProps<SVGSVGElement>>;

export type AmenityIconSource =
  | { type: "hugeicons"; icon: IconSvgElement }
  | { type: "custom"; Icon: AmenityCustomIcon }
  | { type: "image"; src: string };

export function normalizeAmenitySlug(slug: string): string {
  return slug.replace(/[-_\s]/g, "").toLowerCase();
}

const HUGEICONS = {
  bed: { type: "hugeicons", icon: BedIcon },
  bathtub: { type: "hugeicons", icon: Bathtub01Icon },
  parking: { type: "hugeicons", icon: ParkingAreaSquareIcon },
  stairs: { type: "hugeicons", icon: Stairs01Icon },
  home: { type: "hugeicons", icon: Home01Icon },
} as const satisfies Record<string, AmenityIconSource>;

const CUSTOM = {
  cooling: { type: "custom", Icon: CoolingSystemIcon },
  sittingToilet: { type: "custom", Icon: WesternToiletIcon },
  squatToilet: { type: "custom", Icon: IranianToiletIcon },
  jacuzzi: { type: "custom", Icon: JacuzziIcon },
  sauna: { type: "custom", Icon: SaunaIcon },
  pool: { type: "custom", Icon: SwimmingPoolIcon },
  elevator: { type: "custom", Icon: ElevatorIcon },
  internet: { type: "custom", Icon: InternetAndSignalIcon },
} as const satisfies Record<string, AmenityIconSource>;

const IMAGE = {
  heating: {
    type: "image",
    src: "/images/icons/amenities/heating-system.svg",
  },
  traditionalBedding: {
    type: "image",
    src: "/images/icons/amenities/traditional-bedding.svg",
  },
} as const satisfies Record<string, AmenityIconSource>;

export const AMENITY_ICON_MAP: Record<string, AmenityIconSource> = {
  coolingsystem: CUSTOM.cooling,
  heatingsystem: IMAGE.heating,
  sittingtoilet: CUSTOM.sittingToilet,
  squattoilet: CUSTOM.squatToilet,
  shower: HUGEICONS.bathtub,
  bathroom: HUGEICONS.bathtub,
  jacuzzi: CUSTOM.jacuzzi,
  sauna: CUSTOM.sauna,
  pool: CUSTOM.pool,
  wateramenities: CUSTOM.pool,
  bedding: HUGEICONS.bed,
  sleepingarrangements: HUGEICONS.bed,
  traditionalbedding: IMAGE.traditionalBedding,
  parking: HUGEICONS.parking,
  stairs: HUGEICONS.stairs,
  elevator: CUSTOM.elevator,
  internetcoverage: CUSTOM.internet,
  hamrahaval: CUSTOM.internet,
  irancell: CUSTOM.internet,
  rightel: CUSTOM.internet,
};

export function getAmenityIcon(slug: string): AmenityIconSource {
  return AMENITY_ICON_MAP[normalizeAmenitySlug(slug)] ?? HUGEICONS.home;
}
